import React from 'react'
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { InternalAxiosRequestConfigCustom } from '@projtypes/request'
import { AuthTokenResponse } from '@projtypes/token'

export const ACCESS_TOKEN_STORAGE_KEY = 'access-token'
const REFRESH_TOKEN_STORAGE_KEY = 'refresh-token'
const TOKEN_TYPE_STORAGE_KEY = 'token-type'
const ACCESS_TOKEN_EXPIRED_ERROR_CODE = 80002
const DEFAULT_TOKEN_TYPE = 'Bearer'

const backend: { production_URI: string } = {
  production_URI: 'https://api.smartflowmonitoring.com/v3.1',
}

const resolvedBackendUrl = import.meta.env.VITE_APP_API_URI || backend.production_URI

export const getUrl = () => resolvedBackendUrl

export const getTracePropagationTargets = () => ['localhost', getUrl()]

export const getStoredAccessToken = () => localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY)

export const getStoredRefreshToken = () => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)

  if (!refreshToken || refreshToken === 'null') {
    return null
  }

  return refreshToken
}

export const getStoredTokenType = () =>
  localStorage.getItem(TOKEN_TYPE_STORAGE_KEY) ?? DEFAULT_TOKEN_TYPE

export const getAuthorizationHeader = (
  accessToken = getStoredAccessToken(),
  tokenType = getStoredTokenType(),
) => {
  if (!accessToken) {
    return null
  }

  return `${tokenType} ${accessToken}`
}

const updateAxiosDefaultAuthorization = (authorizationHeader: string | null) => {
  if (!authorizationHeader) {
    delete customAxios.defaults.headers.common.Authorization
    return
  }

  customAxios.defaults.headers.common.Authorization = authorizationHeader
}

export const persistAuthSession = (token: AuthTokenResponse) => {
  localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token.access_token)
  localStorage.setItem(TOKEN_TYPE_STORAGE_KEY, token.token_type)

  if (token.refresh_token) {
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token.refresh_token)
  } else {
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY)
  }

  updateAxiosDefaultAuthorization(
    getAuthorizationHeader(token.access_token, token.token_type),
  )
}

export const clearAuthSession = () => {
  localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY)
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY)
  localStorage.removeItem(TOKEN_TYPE_STORAGE_KEY)
  updateAxiosDefaultAuthorization(null)
}

const customAxios: AxiosInstance = axios.create({
  baseURL: getUrl(),
  headers: {
    Accept: 'application/json',
  },
})

updateAxiosDefaultAuthorization(getAuthorizationHeader())

type RefreshFn = () => Promise<AuthTokenResponse | null>
let refreshCallback: RefreshFn | null = null

export const registerRefreshCallback = (fn: RefreshFn) => {
  refreshCallback = fn
}

let refreshPromise: Promise<AuthTokenResponse | null> | null = null

const reqInterceptor = (config: InternalAxiosRequestConfig) => {
  // Token endpoint requests must not carry a bearer token — the server returns
  // 80099 (invalid token) if it receives one on an unauthenticated endpoint.
  // Use AxiosHeaders.delete() — the JS `delete` operator does not work on AxiosHeaders instances.
  if ((config as InternalAxiosRequestConfigCustom)._skipAuthRefresh) {
    config.headers.delete('Authorization')
    return config
  }

  const authorizationHeader = getAuthorizationHeader()

  if (authorizationHeader) {
    config.headers.Authorization = authorizationHeader
  } else {
    delete config.headers.Authorization
  }

  return config
}

const reqErrInterceptor = async (error: AxiosError | Error) => Promise.reject(error)

const resInterceptor = (config: AxiosResponse) => config

const resErrInterceptor = async (error: AxiosError) => {
  const originalRequest: InternalAxiosRequestConfigCustom | undefined = error.config
  const errorCode = (error.response?.data as { error_code?: number } | undefined)?.error_code

  // A credential-less 401 (no error_code, nothing in storage) means another
  // window cleared or rotated the stored tokens. The refresh flow either
  // restores the session or clears it so this window reaches the login screen
  // instead of staying mounted and firing unauthenticated requests forever.
  const isRecoverable401 =
    errorCode === ACCESS_TOKEN_EXPIRED_ERROR_CODE || !getStoredAccessToken()

  if (
    !error.response ||
    error.response.status !== 401 ||
    !isRecoverable401 ||
    !originalRequest ||
    originalRequest._retry ||
    originalRequest._skipAuthRefresh ||
    !refreshCallback
  ) {
    return Promise.reject(error)
  }

  originalRequest._retry = true

  try {
    if (!refreshPromise) {
      refreshPromise = refreshCallback().finally(() => {
        refreshPromise = null
      })
    }

    const token = await refreshPromise
    const authorizationHeader = getAuthorizationHeader(
      token?.access_token,
      token?.token_type,
    )

    if (!authorizationHeader) {
      return Promise.reject(error)
    }

    updateAxiosDefaultAuthorization(authorizationHeader)
    originalRequest.headers.Authorization = authorizationHeader

    return customAxios(originalRequest)
  } catch (refreshError) {
    console.log(refreshError)
    return Promise.reject(refreshError)
  }
}

customAxios.interceptors.request.use(reqInterceptor, reqErrInterceptor)
customAxios.interceptors.response.use(resInterceptor, resErrInterceptor)

// Passthrough — interceptors are now module-level and no longer need a React provider
export const AxiosInterceptor = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  return children as React.ReactElement
}

export default customAxios
